import { Component, TemplateRef, OnInit, ViewChild, EventEmitter, Output, Input, OnDestroy } from '@angular/core';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { timeRequired, dateLessThan } from './../../form-validations/custom.validators';
import { FirestoreService, QueryFilter } from '../../../data/firestore.service';
import { HudlDataService, HudlScheduleEntry, HudlShareEntry, HudlCategories } from 'src/app/data/hudl.service';
import { VidswapTypes } from 'src/app/data/vidswap.service';
import { firstValueFrom, forkJoin, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators'
import { PrivacyOptions } from '../../../data/recording.service';
import { AuthService } from 'src/app/shared/auth.service';
import { RecordingSetup, Team, TeamRecordingSetup, VideoTool } from 'src/app/data/types';
import { EventNameService } from 'src/app/data/event-name.service';

const EVENT_DATE_LOOKUP_DAYS = 14;
const EVENT_DATE_LOOKUP_WHEN_TRAINING_DAYS = 7;

@Component({
    selector: 'app-recording-modal',
    templateUrl: './recording-modal.component.html',
    styleUrls: ['./recording-modal.component.scss'],
    standalone: false
})
export class RecordingModalComponent implements OnInit, OnDestroy {
  private ngUnsubscribe: Subject<any> = new Subject();

  @Input() data = {};
  @Input() teams: Team[] = [];
  recordingForm: UntypedFormGroup;

  @Output() formSubmitted: EventEmitter<any> = new EventEmitter<any>();

  modalRef: BsModalRef;
  minDate: Date | null;
  mytime = new Date();
  config = {
    backdrop: true,
    ignoreBackdropClick: true,
    class: 'modal-right'
  };
  recordingSetups = [];
  showSetupPreview = false; // kept across show() calls: once enabled, the preview follows every configuration picked
  internalVideoToolOnly = false;
  submitted: Boolean;
  hudlEvents: HudlScheduleEntry[];
  hudlAvailableShareList: HudlShareEntry[];
  recordingSetupsLoading: Boolean = false;
  hudlEventsLoading: Boolean = false;
  hudlAvailableShareListLoading: Boolean = false;
  hudlCategories: any[] = HudlCategories;
  vidswapTypes: any[] = VidswapTypes;
  showHudlCategory: Boolean = false;
  hasYoutubeAccount: Boolean = false;
  hasHudlAccount: Boolean = false;
  hasVidswapAccount: Boolean = false;
  videoTool: string = '';
  videoToolVersion: string = '';
  liveStreamProvider: string = '';
  team: any = {};
  teamName: String = '';
  oppTeamName: String = '';
  opponentTeams = [];
  opponentTeamsLoading: Boolean = false;
  privacyOptions = PrivacyOptions;
  isAdmin: boolean = false;
  user: any = {};
  isFirstHudlLoadCompleted: boolean = false;
  replayAvailable: boolean = false;


  @ViewChild('template', { static: true }) template: TemplateRef<any>;

  constructor(private db: FirestoreService, private auth: AuthService, private modalService: BsModalService, private hudlDataService: HudlDataService, private eventNameSvc: EventNameService) { }

  async ngOnInit() {
    this.recordingForm = new UntypedFormGroup({        
      team: new UntypedFormControl(null, [Validators.required]),
      homeTeam: new UntypedFormControl(null),
      hudlEvent: new UntypedFormControl(null),
      hudlSeason: new UntypedFormControl(null),
      eventName: new UntypedFormControl(null, [Validators.required]),
      hudlCategory: new UntypedFormControl(null),
      includeReplayEvent: new UntypedFormControl(false),
      vidswapType: new UntypedFormControl(null),
      liveBroadcast: new UntypedFormControl(null),
      rtmpBroadcastLink: new UntypedFormControl(null),
      youtubeBroadcastKey: new UntypedFormControl(null),
      videoPrivacy: new UntypedFormControl(null),
      opponentTeam: new UntypedFormControl(null),
      recordingSetup: new UntypedFormControl(null, [Validators.required]),
      hudlShare: new UntypedFormControl(null),
      date: new UntypedFormControl(null, [Validators.required]),
      dateSpan: new UntypedFormGroup({
        start: new UntypedFormControl(null, [timeRequired()]),
        end: new UntypedFormControl(null, [timeRequired()])
      }, dateLessThan('start', 'end'))
    });
    
    this.isAdmin = await this.auth.hasRole('admin');
    this.user = await this.db.get('users', this.auth.user.uid).pipe(take(1)).toPromise();
    this.submitted = false;
    if(this.isAdmin){
      this.minDate = null;
    }
    else{
      this.minDate = new Date();
      this.minDate.setHours(this.minDate.getHours() - 1 * 24);
    }

    this.onChanges();
  }

  ngOnDestroy(){
    this.ngUnsubscribe.next(null);
    this.ngUnsubscribe.complete();
  }

  onChanges() {
    let teamRecordingSetupSubscription, hudlEventsSubscription, videoToolSubscription, hudlShareSubscription, opponentTeamsSubscription;
    this.recordingForm.get('team').valueChanges.subscribe(value => {
      this.recordingSetups = [];
      this.hudlEvents = [];
      this.hudlAvailableShareList = [];
      this.recordingForm.patchValue({
        homeTeam: null,
        hudlEvent: null,
        hudlSeason: null,
        liveBroadcast: null,
        opponentTeam: null,
        recordingSetup: null,
        hudlShare: null,
      });

      if(!value){
        return;
      }

      this.hudlEventsLoading = true;
      this.recordingSetupsLoading = true;
      this.hudlAvailableShareListLoading = true;
      this.opponentTeamsLoading = true;

      const team = this.teams.find(team =>{
        return team.path === value;
      })
      this.team = team;
      this.replayAvailable = Boolean(team.replayEnabled);
      this.recordingForm.get('includeReplayEvent').patchValue(
        this.replayAvailable && this.recordingForm.get('hudlCategory').value === 4
      );
      this.hasYoutubeAccount = Boolean(team.youtubeAccount);
      this.hasHudlAccount = Boolean(team.hudlTeamId);
      this.hasVidswapAccount = Boolean(team.vidswapTeamId);
      this.liveStreamProvider = team.liveStreamProvider ?? 'youtube';
      if (this.liveStreamProvider === 'rtmp' && this.recordingForm.get('liveBroadcast').value) {
        this.recordingForm.get('rtmpBroadcastLink').setValidators([Validators.required]);
      } else {
        this.recordingForm.get('rtmpBroadcastLink').clearValidators();
      }
      this.recordingForm.get('rtmpBroadcastLink').updateValueAndValidity();
      this.teamName = team.name;
      this.updateEventName();
      
      if(videoToolSubscription){
        videoToolSubscription.unsubscribe();
      }
      if(team.videoTool){
        videoToolSubscription = this.db.get<VideoTool>('videoTools', team.videoTool.v.id).pipe(takeUntil(this.ngUnsubscribe)).subscribe(
          data => {
            if (data && data.tool) {
              this.videoTool = data.tool;
            }
            if(data && data.version){
              this.videoToolVersion = data.version;
            }
          },
          error => {
            console.error(error);
          }
        );
      }
      else{
        this.videoTool = '';
      }

      if(teamRecordingSetupSubscription){
        teamRecordingSetupSubscription.unsubscribe();
      }
      const capturedTeamPath = team.path;
      const isStillCurrent = () => this.recordingForm.get('team').value === capturedTeamPath;
      teamRecordingSetupSubscription = this.db.get<TeamRecordingSetup>('teamRecordingSetups', team.id).pipe(takeUntil(this.ngUnsubscribe)).subscribe(
        data => {
          if (!isStillCurrent()) {
            return;
          }
          const refs = data?.recordingSetups ?? [];
          if (refs.length === 0) {
            this.recordingSetups = [];
            this.recordingSetupsLoading = false;
            return;
          }
          const selected = this.recordingForm.get('recordingSetup').value;
          const selectedPath = selected?.v?.path ?? null;
          forkJoin(refs.map(ref => this.db.getByPath<RecordingSetup>(ref.v.path).pipe(take(1)))).pipe(takeUntil(this.ngUnsubscribe)).subscribe({
            next: setups => {
              if (!isStillCurrent()) {
                return;
              }
              this.recordingSetups = refs.filter((ref, i) => setups[i]?.active === true || ref.v.path === selectedPath);
              this.recordingSetupsLoading = false;
            },
            error: err => {
              if (!isStillCurrent()) {
                return;
              }
              console.error(err);
              this.recordingSetups = refs;
              this.recordingSetupsLoading = false;
            }
          });
        },
        error => {
          console.error(error);
        }
      );

      if(hudlEventsSubscription){
        hudlEventsSubscription.unsubscribe();
      }
      hudlEventsSubscription = this.hudlDataService.loadEvents(team.path).pipe(takeUntil(this.ngUnsubscribe)).subscribe({
        next: (events) => {
          this.hudlEvents = events;
          this.hudlEventsLoading = false;
          this.selectClosestHudlEvent();
        },
        error: (err) => console.error(err),
      });

      if(hudlShareSubscription){
        hudlShareSubscription.unsubscribe();
      }
      this.isFirstHudlLoadCompleted = false;
      hudlShareSubscription = this.hudlDataService.loadShareList(team.path).pipe(takeUntil(this.ngUnsubscribe)).subscribe({
        next: ({ list }) => {
          this.hudlAvailableShareList = list;
          if (!this.isFirstHudlLoadCompleted) {
            const defaults = this.hudlDataService.computeDefaultShare(this.team, this.user, list);
            this.recordingForm.get('hudlShare').patchValue(defaults);
            this.isFirstHudlLoadCompleted = true;
          }
          this.hudlAvailableShareListLoading = false;
        },
        error: (err) => console.error(err),
      });

      if(opponentTeamsSubscription){
        opponentTeamsSubscription.unsubscribe();
      }
      const opponentTeamsFilter: QueryFilter[] = team.isLocation ? [] : [['sport', '==', team.sport], ['level', '==', team.level], ['gender', '==', team.gender]];
      opponentTeamsSubscription = this.db.list<Team>('teams', opponentTeamsFilter).pipe(takeUntil(this.ngUnsubscribe)).subscribe(
        teams => {
          if (teams) {
            const usedTeamsPaths = this.opponentTeams.filter(team => [this.recordingForm.get('opponentTeam').value?.path, this.recordingForm.get('homeTeam').value?.path].includes(team.path)).map(team => team.path);
            this.opponentTeams = usedTeamsPaths.concat(teams.filter(team => team.organization && team.name && !usedTeamsPaths.includes(team.path)).map(team =>{
              return {
                ...team,
                displayValue: `${team.organization.d} ${team.name}`
              }
            }));
            this.opponentTeamsLoading = false;
          }
        },
        error => {
          console.error(error);
        }
      );
    });

    this.recordingForm.get('recordingSetup').valueChanges.subscribe(async(value) => {
      if(!value){
        return;
      }
      const recordingSetup = await firstValueFrom(this.db.getByPath<RecordingSetup>(value.path));
      this.internalVideoToolOnly = recordingSetup.detectionMode?.v.id === 'equirectangular';
      this.recordingForm.patchValue({videoPrivacy: 'private'});
    });

    this.recordingForm.get('hudlEvent').valueChanges.subscribe(value => {
      if(!value){
        this.recordingForm.get('date').patchValue(null);
        this.recordingForm.get('hudlSeason').patchValue(null);
        this.showHudlCategory = false;
        return;
      }
      const hudlEvent = this.hudlEvents.find(event => {
        return event.id === value;
      });
      this.recordingForm.get('hudlSeason').patchValue(hudlEvent.seasonId);

      if(hudlEvent.opponentId){
        const opponentTeam = this.opponentTeams.find(opponentTeam => {
          return opponentTeam.hudlTeamId == hudlEvent.opponentId;
        });
        if(opponentTeam){
          this.recordingForm.patchValue({opponentTeam: opponentTeam.path});
        }
        else{
          this.db.getFirst<Team>('teams', [['hudlTeamId', '==', hudlEvent.opponentId]]).pipe(take(1)).subscribe(
            data => {
              if(data && data.id){
                this.opponentTeams.push({
                  ...data,
                  displayValue: `${data.organization.d} ${data.name}`
                });
                this.recordingForm.patchValue({opponentTeam: data.path});
              }
            }
          );
        }
      }
      else{
        this.recordingForm.patchValue({opponentTeam: null});
      }
      if(!this.recordingForm.get('opponentTeam').value){
        this.oppTeamName = hudlEvent.name;
      }
      this.updateEventName();
      if(this.recordingForm.get('hudlCategory').value === null || hudlEvent.isPractice){
        this.recordingForm.get('hudlCategory').patchValue(5); //Always set practice as default
      }
      if(this.recordingForm.get('hudlCategory').value === 4){
        this.recordingForm.get('date').patchValue(hudlEvent.time);
      }
      this.showHudlCategory = hudlEvent.isPractice ? false : true;
    });

    this.recordingForm.get('hudlCategory').valueChanges.subscribe(value => {
      this.recordingForm.get('includeReplayEvent').patchValue(this.replayAvailable && value === 4);
      if(value === 4){
        const hudlEvent = this.hudlEvents.find(event => {
          return event.id === this.recordingForm.get('hudlEvent').value;
        });
        this.recordingForm.get('date').patchValue(hudlEvent.time);
      }
      this.updateEventName();
    });

    ['date', 'homeTeam', 'opponentTeam'].forEach(fieldName =>{
      this.recordingForm.get(fieldName).valueChanges.subscribe(value => {
        this.updateEventName();
      });
    })
    this.recordingForm.get('dateSpan').get('start').valueChanges.subscribe(value => {
      this.updateEventName();
    });

    this.recordingForm.get('liveBroadcast').valueChanges.subscribe(value => {
      if(value === true){
        this.recordingForm.get('videoPrivacy').patchValue('public');
        if (this.liveStreamProvider === 'rtmp') {
          this.recordingForm.get('rtmpBroadcastLink').setValidators([Validators.required]);
        }
      }
      else{
        this.recordingForm.get('videoPrivacy').patchValue('unlisted');
        this.recordingForm.get('rtmpBroadcastLink').clearValidators();
      }
      this.recordingForm.get('rtmpBroadcastLink').updateValueAndValidity();
    });
  }

  updateEventName() {
    if(this.recordingForm.get('opponentTeam').value){
      const opponentTeam = this.opponentTeams.find(team => team.path == this.recordingForm.get('opponentTeam').value);
      this.oppTeamName = opponentTeam.organization.d;
    }
    if(this.recordingForm.get('homeTeam').value){
      const homeTeam = this.opponentTeams.find(team => team.path == this.recordingForm.get('homeTeam').value);
      this.teamName = homeTeam.organization.d;
    }

    const eventName = this.eventNameSvc.buildEventName({
      videoTool: this.videoTool,
      hudlCategory: this.recordingForm.get('hudlCategory').value,
      liveBroadcast: !!this.recordingForm.get('liveBroadcast').value,
      teamName: this.teamName as string,
      oppTeamName: this.oppTeamName as string,
      date: this.recordingForm.get('date').value as Date | null,
      start: new Date(this.recordingForm.get('dateSpan').get('start').value),
    });
    this.recordingForm.get('eventName').patchValue(eventName);
  }

  get isPastDateTime(): boolean {
    const date = this.recordingForm?.get('date')?.value as Date;
    if(!date){
      return false;
    }
    const start = this.recordingForm?.get('dateSpan')?.get('start')?.value as Date;
    if(!start){
      const startOfToday = new Date();
      startOfToday.setHours(0, 0, 0, 0);
      const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      return dateOnly.getTime() < startOfToday.getTime();
    }
    const combined = new Date(date.getFullYear(), date.getMonth(), date.getDate(),
      start.getHours(), start.getMinutes(), 0, 0);
    return combined.getTime() < Date.now();
  }

  selectClosestHudlEvent() {
    const closest = this.hudlDataService.pickClosestEvent(this.hudlEvents, EVENT_DATE_LOOKUP_WHEN_TRAINING_DAYS, EVENT_DATE_LOOKUP_DAYS);
    this.recordingForm.get('hudlEvent').patchValue(closest ? closest.id : null);
  }

  show() {
    //Reinitialize dynamic lists
    this.recordingSetups = [];
    this.hudlEvents = [];
    this.hudlAvailableShareList = [];
    //Resinitialize form
    this.recordingForm.reset();
    this.replayAvailable = false;
    //Set default values
    if(this.teams.length === 1){
      this.recordingForm.get('team').patchValue(this.teams[0].path);
    }
    this.recordingForm.patchValue({videoPrivacy: 'unlisted'});
    this.submitted = false;

    this.modalRef = this.modalService.show(this.template, this.config);
  }

  async refreshHudlEvents(){
    this.hudlEventsLoading = true;
    await this.hudlDataService.refreshData(this.recordingForm.get('team').value.split('/').at(-1), ['schedule']);
    this.hudlEventsLoading = false;
  }

  async refreshHudlMembersAndGroups(){
    this.hudlAvailableShareListLoading = true;
    await this.hudlDataService.refreshData(this.recordingForm.get('team').value.split('/').at(-1), ['membersAndGroups']);
    this.hudlAvailableShareListLoading = false;
  }

  onSubmit(){
    this.submitted = true;
    if(this.recordingForm.valid){
      let data = this.recordingForm.value;
      ['start', 'end'].forEach(function(field){
        data[field] = data.dateSpan[field];
        data[field].setFullYear(data.date.getFullYear());
        data[field].setMonth(data.date.getMonth());
        data[field].setDate(data.date.getDate());
        data[field].setMilliseconds(0);
      });
      delete data.dateSpan;
      delete data.date;
      ['opponentTeam', 'homeTeam', 'team'].forEach(field =>{
        if(data[field]){
          data[field] = this.db.getDocumentReference(data[field]);
        }
      });
      if(this.internalVideoToolOnly){
        data.hudlEvent = null;
        data.hudlSeason = null;
        data.hudlCategory = null;
        data.hudlShare = null;
      }
      data.includeReplayEvent = Boolean(this.replayAvailable && data.includeReplayEvent);
      data.liveBroadcast = Boolean(data.liveBroadcast && !this.internalVideoToolOnly && (!this.showHudlCategory || [4,5].indexOf(data.hudlCategory) > -1));
      if(this.liveStreamProvider == 'youtube'){
        data.rtmpBroadcastLink = null;
        if(!this.hasYoutubeAccount){
          data.liveBroadcast = false;
        }
      }
      data.opponentTeam = data.liveBroadcast ? data.opponentTeam : null;
      data.homeTeam = data.liveBroadcast && this.team.isLocation ? data.homeTeam : null;
      this.formSubmitted.emit(data);
      this.modalRef.hide();
    }
  }

}
